import ActiveTeamDisplay from "_shared/teams/active-team-display";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Guid from "_shared/guids/guid";
import { workAgreementConversationPath } from "messaging/conversations/conversation-links";
import {
  ActiveTeamJSON,
  ActiveTeamPerspective,
  ActiveTeamSide,
} from "work/work-agreements/teams/active-team/_schemas/active-team-schema";
import { PageTab as WorkAgreementPageTab } from "work/work-agreements/_views/view/view-work-agreement-page-tabs";
import { WorkAgreementConversation } from "work/work-agreements/correspondence/work-agreement-conversation";

/**
 * A team on a hired, still-running work agreement, with the agreement it is on (#4441).
 *
 * Every active team belongs to exactly one agreement, and the agreement is where the team's chat is,
 * so the Teams page names the work beside the team and opens the agreement's Correspondence pane.
 * One agreement can be two rows of the page, its client team and its vendor team (#6557), so a row
 * is its agreement and its side.
 */
export default class ActiveTeam implements ActiveTeamDisplay {
  public static fromJSON(json: ActiveTeamJSON | null | undefined): ActiveTeam | undefined {
    if (!json) return undefined;

    return new ActiveTeam({
      leader: EntityMember.fromJSON(json.leader) ?? undefined,
      members: EntityMember.fromJSONArray({ jsonArray: json.members }) ?? [],
      workAgreementId: Guid.create(json.workAgreementId),
      workAgreementName: json.workAgreementName,
      versionNumber: json.versionNumber ?? undefined,
      hireDate: new AHDate(json.hireDate),
      side: json.side,
      perspectives: json.perspectives,
    });
  }

  public readonly leader?: EntityMember;
  public readonly members: EntityMember[];
  public readonly workAgreementId: Guid;
  public readonly workAgreementName: string;
  /** Which version of the work the agreement is (#6636). Undefined on a payload older than the field. */
  public readonly versionNumber?: number;
  /** When the work was hired, which is when the team became an active one. */
  public readonly hireDate: AHDate;
  /** Which side of the work the team is on. */
  public readonly side: ActiveTeamSide;
  /** Why the team is on the reader's page. */
  public readonly perspectives: ActiveTeamPerspective[];

  /** The row's key: two rows of the same work differ only in their side. */
  public get rowId(): string {
    return `${this.workAgreementId.value}:${this.side}`;
  }

  /**
   * The room this team's row opens (#4441). A team member opens their own team's room. A client
   * whose vendor team works for them is not on that team and cannot read its room, so the row opens
   * the room both sides read.
   */
  public get conversation(): WorkAgreementConversation {
    if (this.side === "Client") return WorkAgreementConversation.ClientTeam;
    if (this.perspectives.includes("On")) return WorkAgreementConversation.VendorTeam;
    return WorkAgreementConversation.AllStakeholders;
  }

  /**
   * The team's own tab on its agreement, which is where a row of the Teams page opens (#6718): **Client
   * Team** for the client's team and **Vendor Team** for the firm's. Each side can read both tabs.
   */
  public get teamPath(): string {
    const tab = this.side === "Client" ? WorkAgreementPageTab.ClientTeam : WorkAgreementPageTab.Team;
    return `/work/active/view/${this.workAgreementId.value}/${tab}`;
  }

  /** What the team is called on its row: its side of the work. */
  public get sideLabel(): string {
    return this.side === "Client" ? "Client team" : "Vendor team";
  }

  /** The agreement's page, with its Correspondence pane open on this team's room. */
  public get conversationPath(): string {
    return workAgreementConversationPath({ workAgreementId: this.workAgreementId.value, forum: this.conversation });
  }

  public constructor(params: {
    leader?: EntityMember;
    members: EntityMember[];
    workAgreementId: Guid;
    workAgreementName: string;
    versionNumber?: number;
    hireDate: AHDate;
    side: ActiveTeamSide;
    perspectives: ActiveTeamPerspective[];
  }) {
    this.leader = params.leader;
    this.members = params.members;
    this.workAgreementId = params.workAgreementId;
    this.workAgreementName = params.workAgreementName;
    this.versionNumber = params.versionNumber;
    this.hireDate = params.hireDate;
    this.side = params.side;
    this.perspectives = params.perspectives;
  }
}
